import {
  buildGroupRanges,
  consolidationGroupLabelsPlugin,
} from './consolidationGroupLabelsPlugin';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  consolidationGroupLabelsPlugin
);

const FEMALE_COLOR = '#F5C5D5';
const MALE_COLOR = '#E2F2FF';

/**
 * Flatten consolidation chartData to labels + female/male datasets.
 * chartData: [ { category, items: [ { label, femalePct, malePct } ] } ]
 */
function flattenChartData(chartData) {
  const labels = [];
  const femaleData = [];
  const maleData = [];
  (chartData || []).forEach((cat) => {
    (cat.items || []).forEach((sub) => {
      labels.push(sub.label || '');
      femaleData.push(Math.min(Number(sub.femalePct) || 0, 100));
      maleData.push(Math.min(Number(sub.malePct) || 0, 100));
    });
  });
  return { labels, femaleData, maleData };
}

function niceCeil(value, step) {
  if (!Number.isFinite(value) || value <= 0) return step;
  return Math.ceil(value / step) * step;
}

const HorizontalBarChart = ({
  chartData,
  onDone,
  animationDuration = 0,
  maxPct = null,
  fontSize = 9,
  /** Optional: font size for group labels (e.g. Blood Pressure, BMI). If not set, PDF-friendly small size is used. */
  groupLabelFontSize,
}) => {
  const { labels, femaleData, maleData } = useMemo(
    () => flattenChartData(chartData),
    [chartData]
  );

  const computedMaxPct = useMemo(() => {
    if (Number.isFinite(maxPct) && maxPct > 0) return maxPct;
    const maxValue = Math.max(0, ...femaleData, ...maleData);
    // Keep a sensible minimum and nice 10% ticks.
    return Math.min(100, Math.max(10, niceCeil(maxValue, 10)));
  }, [maxPct, femaleData, maleData]);

  const groupRanges = useMemo(() => buildGroupRanges(chartData), [chartData]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Female',
          data: femaleData,
          backgroundColor: FEMALE_COLOR,
        },
        {
          label: 'Male',
          data: maleData,
          backgroundColor: MALE_COLOR,
        },
      ],
    }),
    [labels, femaleData, maleData]
  );

  const options = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: 2,
      layout: {
        padding: { left: 64, right: 40 },
      },
      animation: { duration: animationDuration },
      onComplete: onDone ? () => {} : undefined,
      scales: {
        x: {
          max: computedMaxPct,
          grace: '7%',
          grid: { drawBorder: false },
          ticks: {
            font: { family: 'OpenSans', size: fontSize },
            callback: (value) => (value != null ? `${value}%` : ''),
          },
        },
        y: {
          grid: { display: false },
          barPercentage: 0.55,
          categoryPercentage: 0.6,
          ticks: {
            font: { family: 'OpenSans', size: fontSize },
            autoSkip: false,
            padding: 4,
          },
        },
      },
      plugins: {
        ...(groupRanges.length ? { consolidationGroups: groupRanges } : {}),
        consolidationGroupLabelFontSize:
          groupLabelFontSize ?? Math.min(fontSize, 7),
        consolidationGroupLabelLineHeight:
          groupLabelFontSize != null ? Math.max(7, groupLabelFontSize + 1) : 7,
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%`,
          },
        },
        datalabels: {
          anchor: 'end',
          align: 'end',
          offset: 2,
          clamp: true,
          font: { family: 'OpenSans', size: fontSize - 1 },
          color: '#333',
          formatter: (value) => (value > 0 ? `${value}%` : ''),
        },
      },
    }),
    [
      animationDuration,
      computedMaxPct,
      fontSize,
      groupLabelFontSize,
      onDone,
      groupRanges,
    ]
  );

  const optionsWithOnComplete = useMemo(() => {
    if (!onDone) return options;
    return {
      ...options,
      animation: {
        ...options.animation,
        onComplete: (e) => onDone(e?.chart ?? e),
      },
    };
  }, [options, onDone]);

  if (!labels.length) return null;

  return (
    <Bar
      data={data}
      options={optionsWithOnComplete}
      plugins={[ChartDataLabels]}
    />
  );
};

export default HorizontalBarChart;
